import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Check, X, Crown } from "lucide-react";

const OWNER_PLANS = [
  {
    id: "particular-1",
    name: "BÁSICO",
    price: "$600 MXN",
    duration: "1 mes",
    type: "Particular",
    title: "Publica 1 propiedad",
    titleSuffix: " por un mes",
    headerBg: "bg-[#CAA9F5]",
    headerTextColor: "text-black",
    features: [
      { text: "1 usuario", included: true },
      { text: "App Casas y Terrenos PRO", included: true },
      { text: "Fichas técnicas de propiedades", included: true },
      { text: "Reportes", included: true },
      { text: "Destacado", included: false },
      { text: "Conexión a CRMs", included: false },
    ],
    idPlan: "particular-1",
    priceId: "price_1SnSdaHGBw2VKdDPGG3FKZr5",
  },
  {
    id: "particular-destacado",
    name: "DESTACADO",
    price: "$1,400 MXN",
    oldPrice: "$1,600",
    duration: "3 meses",
    type: "Particular",
    title: "Publica 1 propiedad",
    titleSuffix: " destacada, con alta visibilidad en los resultados de búsqueda por 3 meses",
    highlightSuffix: "destacada",
    headerBg: "bg-black",
    headerTextColor: "text-white",
    isPopular: true,
    features: [
      { text: "1 usuario", included: true },
      { text: "1 destacado", included: true },
      { text: "App Casas y Terrenos PRO", included: true },
      { text: "Fichas técnicas de propiedades", included: true },
      { text: "Reportes", included: true },
      { text: "Conexión a CRMs", included: false },
    ],
    idPlan: "particular-destacado",
    priceId: "price_1SD8s7HGBw2VKdDP7zDW3lqZ",
  },
  {
    id: "particular-3",
    name: "ESTÁNDAR",
    price: "$1,000 MXN",
    duration: "3 meses",
    type: "Particular",
    title: "Publica 1 propiedad",
    titleSuffix: " por 3 meses",
    headerBg: "bg-[#5CD495]",
    headerTextColor: "text-black",
    features: [
      { text: "1 usuario", included: true },
      { text: "App Casas y Terrenos PRO", included: true },
      { text: "Fichas técnicas de propiedades", included: true },
      { text: "Reportes", included: true },
      { text: "Destacado", included: false },
      { text: "Conexión a CRMs", included: false },
    ],
    idPlan: "particular-3",
    priceId: "price_1SD8srHGBw2VKdDPj6z2Q4Dh",
  },
];

export const PlanCard = ({ plan, selectedPlan, onSelectPlan }) => {
  const {
    name,
    price,
    oldPrice,
    duration,
    type,
    title,
    titleSuffix,
    highlightSuffix,
    headerBg,
    headerTextColor,
    isPopular,
    features,
    idPlan,
    contactLabel,
    contactUrl,
  } = plan;
  const isSelected = selectedPlan === idPlan;

  const handleCardClick = () => {
    if (contactUrl || !onSelectPlan) return;

    onSelectPlan(idPlan);
  };

  const renderTitle = () => {
    if (!titleSuffix) return title;

    if (highlightSuffix && titleSuffix.includes(highlightSuffix)) {
      const parts = titleSuffix.split(highlightSuffix);
      return (
        <>
          {title} {parts[0]}
          <span className="text-[#A855F7]">{highlightSuffix}</span>
          {parts[1] || ""}
        </>
      );
    }

    return (
      <>
        {title}
        {titleSuffix}
      </>
    );
  };

  return (
    <div
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (!contactUrl && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleCardClick();
        }
      }}
      role={contactUrl ? undefined : "button"}
      tabIndex={contactUrl ? undefined : 0}
      className={classNames(
        "relative transition-all duration-300 transform h-full w-full justify-self-center max-w-[400px] flex flex-col rounded-xl overflow-hidden border-2 focus:outline-none",
        !contactUrl && "cursor-pointer hover:scale-[1.01]",
        isPopular ? "border-black z-10" : "border-transparent shadow-lg",
        isSelected
          ? "ring-4 ring-[#8B4AFF] ring-offset-2 scale-[1.02] border-transparent z-20"
          : "",
      )}
    >
      {isSelected && (
        <div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-30">
          <div className="bg-[#8B4AFF] text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg whitespace-nowrap uppercase">
            Seleccionado
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className={classNames(
          "py-4 px-6 flex justify-between items-center z-10 border-b border-black/5",
          headerBg,
          headerTextColor,
        )}
      >
        <h3 className="text-xl font-bold mb-0 tracking-wide">{name}</h3>
        {isPopular && (
          <div className="flex items-center text-[10px] font-bold gap-1 uppercase tracking-wider bg-white/20 px-2 py-1 rounded-full">
            <Crown className="w-3.5 h-3.5" />
            Mejor valor
          </div>
        )}
      </div>

      {/* Body */}
      <div
        className={classNames(
          isPopular ? "shadow-[inset_0px_-50px_39px_-37px_#A4B8B8]" : "",
          "bg-white p-6 pb-8 flex flex-col flex-1 relative",
        )}
      >
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm font-semibold text-gray-700 tracking-wider">{duration}</span>
          <span className="text-sm text-gray-500 font-medium tracking-wide">{type}</span>
        </div>

        <h4 className="text-[17px] font-bold leading-tight mb-5 text-black">{renderTitle()}</h4>

        <hr className="mb-5 border-gray-200" />

        <div className="space-y-3 mb-8 flex-1">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3">
              {feature.included ? (
                <div className="mt-0.5 flex-shrink-0 w-4 h-4 bg-black rounded-full flex items-center justify-center p-0.5">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />
                </div>
              ) : (
                <div className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center">
                  <X className="w-2.5 h-2.5 text-gray-300" strokeWidth={3} />
                </div>
              )}
              <span
                className={classNames(
                  "text-[13px] font-medium leading-tight",
                  feature.included ? "text-black" : "text-gray-400 line-through",
                )}
              >
                {feature.text}
              </span>
            </div>
          ))}
        </div>

        {/* Pricing Box */}
        <div
          className={classNames(
            "rounded-xl border p-4 flex flex-col items-center justify-center transition-all",
            isSelected
              ? "bg-black border-black text-white shadow-lg"
              : isPopular
                ? "bg-gray-100 border-gray-300 hover:bg-gray-200"
                : "bg-gray-50 border-gray-200 hover:bg-gray-100",
          )}
        >
          {contactUrl ? (
            <a
              href={contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className={classNames(
                "text-center text-sm font-bold uppercase tracking-wider underline",
                isSelected ? "text-white" : "text-gray-700",
              )}
            >
              {contactLabel || "Hablar con un ejecutivo"}
            </a>
          ) : (
            <>
              <div className="flex items-baseline gap-2">
                <span
                  className={classNames(
                    "text-2xl font-black",
                    isSelected ? "text-white" : "text-black",
                  )}
                >
                  {price}
                </span>
                {oldPrice && (
                  <span
                    className={classNames(
                      "text-sm line-through",
                      isSelected ? "text-gray-400" : "text-gray-500",
                    )}
                  >
                    {oldPrice}
                  </span>
                )}
              </div>
              <span
                className={classNames(
                  "text-[10px] mt-1 font-bold uppercase tracking-wider",
                  isSelected ? "text-gray-300" : "text-gray-500",
                )}
              >
                {isSelected ? "Seleccionado" : "Click para seleccionar"}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function Plans({ selectedPlan, onSelectPlan }) {
  return (
    <div className="w-full h-full overflow-y-auto max-h-[85vh] px-4 ">
      <div className="max-w-7xl mx-auto pt-8">
        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4 justify-items-center">
          {OWNER_PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              selectedPlan={selectedPlan}
              onSelectPlan={onSelectPlan}
            />
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm bg-gray-50 py-3 px-8 rounded-2xl border border-gray-100 inline-block">
            <span className="font-bold text-gray-700">*</span> Estos planes son de{" "}
            <span className="font-bold text-gray-800">pago único</span> y vencen automáticamente
            según la duración indicada en cada plan.
          </p>
        </div>
      </div>
    </div>
  );
}

PlanCard.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.string,
    contactLabel: PropTypes.string,
    contactUrl: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.string,
    oldPrice: PropTypes.string,
    duration: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    titleSuffix: PropTypes.string,
    highlightSuffix: PropTypes.string,
    headerBg: PropTypes.string,
    headerTextColor: PropTypes.string,
    isPopular: PropTypes.bool,
    features: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        included: PropTypes.bool,
      }),
    ),
    idPlan: PropTypes.string,
    priceId: PropTypes.string,
  }).isRequired,
  selectedPlan: PropTypes.string,
  onSelectPlan: PropTypes.func,
};

Plans.propTypes = {
  selectedPlan: PropTypes.string,
  onSelectPlan: PropTypes.func,
};
